/**
 * Engraving Toggle Functionality
 * Handles the toggle switch for showing/hiding engraving options
 */

class EngravingToggle {
  constructor() {
    this.toggle = document.getElementById('engraving-toggle');
    this.engravingWrapper = document.querySelector('.engraving-text-wrapper');
    this.engravingSwatchContainer = this.findEngravingSwatchContainer();
    this.swatchInputs = document.querySelectorAll('.swatches__form--input');
    this.engravingTextInput = document.querySelector('.engraving-text');
    this.engravingFontInputs = document.querySelectorAll('input[name="properties[Engraving Font]"]');
    
    this.init();
  }

  findEngravingSwatchContainer() {
    // Find the swatch container that contains engraving options
    const swatchContainers = document.querySelectorAll('.swatches__container');
    for (let container of swatchContainers) {
      const optionName = container.querySelector('.swatches__option-name');
      if (optionName && optionName.textContent.toLowerCase().includes('engraving')) {
        return container;
      }
    }
    return null;
  }

  init() {
    if (!this.toggle || !this.engravingWrapper) {
      return; // Exit if required elements don't exist
    }

    // Set initial state - hide engraving options by default
    this.setInitialState();
    this.bindEvents();
  }

  setInitialState() {
    // Ensure engraving options are hidden by default
    if (this.engravingWrapper) {
      this.engravingWrapper.classList.add('dn');
    }
    if (this.engravingSwatchContainer) {
      this.engravingSwatchContainer.classList.add('dn');
    }
    this.toggle.checked = false;
    
    // Set initial font text (Block is default)
    this.updateFontText('Block');
  }

  bindEvents() {
    // Handle toggle switch changes
    this.toggle.addEventListener('change', (e) => {
      this.handleToggleChange(e.target.checked);
    });

    // Handle swatch input changes
    this.swatchInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.handleSwatchChange(e.target);
      });
    });

    // Handle font selection changes
    this.engravingFontInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.handleFontChange(e.target);
      });
    });
  }

  handleToggleChange(isChecked) {
    if (isChecked) {
      this.showEngravingOptions();
      this.selectEngravingOption(true);
    } else {
      this.hideEngravingOptions();
      this.clearEngravingData();
      this.selectEngravingOption(false);
    }
  }

  handleSwatchChange(input) {
    const value = input.value.toLowerCase();
    const engravingValues = ['engraving', 'with engraving'];
    
    // Check if this is an engraving swatch input
    const isEngravingSwatch = input.closest('.swatches__container') === this.engravingSwatchContainer;
    
    if (isEngravingSwatch) {
      // This is an engraving swatch selection
      const hasEngraving = value.includes('with') && value.includes('engraving') || 
                          (value.includes('engraving') && !value.includes('no') && !value.includes('without'));
      
      if (hasEngraving) {
        this.showEngravingOptions();
        this.toggle.checked = true;
      } else {
        this.hideEngravingOptions();
        this.toggle.checked = false;
        this.clearEngravingData();
      }
    } else if (engravingValues.includes(value)) {
      // This is a non-engraving swatch but has engraving in the value
      this.showEngravingOptions();
      this.toggle.checked = true;
    } else {
      // This is a non-engraving swatch
      this.hideEngravingOptions();
      this.toggle.checked = false;
    }
  }

  handleFontChange(input) {
    const selectedFont = input.value;
    this.updateFontText(selectedFont);
  }

  updateFontText(fontType) {
    // Find all font value spans in engraving swatch containers
    const fontValueSpans = document.querySelectorAll('.swatches__font-value');
    
    fontValueSpans.forEach(span => {
      if (fontType === 'Block') {
        span.textContent = 'Block Engraving';
      } else if (fontType === 'Script') {
        span.textContent = 'Script Engraving';
      } else {
        span.textContent = '';
      }
    });
  }

  selectEngravingOption(withEngraving) {
    if (!this.engravingSwatchContainer) {
      return;
    }

    // Find all engraving swatch inputs
    const engravingInputs = this.engravingSwatchContainer.querySelectorAll('.swatches__form--input');
    
    engravingInputs.forEach(input => {
      const value = input.value.toLowerCase();
      
      if (withEngraving) {
        // Select "WITH ENGRAVING" or similar positive engraving options
        if (value.includes('with') && value.includes('engraving') || 
            value.includes('engraving') && !value.includes('no') && !value.includes('without')) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // Select "NO ENGRAVING" or similar negative engraving options
        if (value.includes('no') && value.includes('engraving') || 
            value.includes('without') && value.includes('engraving') ||
            value === 'no engraving' || value === 'without engraving') {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  showEngravingOptions() {
    if (this.engravingWrapper) {
      this.engravingWrapper.classList.remove('dn');
    }
    if (this.engravingSwatchContainer) {
      this.engravingSwatchContainer.classList.remove('dn');
    }
  }

  hideEngravingOptions() {
    if (this.engravingWrapper) {
      this.engravingWrapper.classList.add('dn');
    }
    if (this.engravingSwatchContainer) {
      this.engravingSwatchContainer.classList.add('dn');
    }
  }

  clearEngravingData() {
    // Clear engraving text input
    if (this.engravingTextInput) {
      this.engravingTextInput.value = '';
    }

    // Reset font selection to default (Block)
    this.engravingFontInputs.forEach(input => {
      input.checked = false;
    });
    
    // Set Block as default
    const blockInput = document.getElementById('engraving-font-block');
    if (blockInput) {
      blockInput.checked = true;
      this.updateFontText('Block');
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new EngravingToggle();
});

// Also initialize if DOM is already loaded (for dynamic content)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EngravingToggle();
  });
} else {
  new EngravingToggle();
}
